"use client"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import type { ShareSegment } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { useTranslations } from "@/i18n"
import { cn } from "@ibada/ui"
import { FALLBACK_MUTED_CLASS, type TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"


/** One color per named branch, the grouped "others" in slate. The brand green is the first, used once. */
const SEGMENT_COLORS = [
  "bg-emerald-600 dark:bg-emerald-500",
  "bg-teal-500 dark:bg-teal-400",
  "bg-violet-600 dark:bg-violet-400",
  "bg-amber-500 dark:bg-amber-400",
]
const OTHERS_COLOR = "bg-slate-400 dark:bg-slate-500"

const colorOf = (segment: ShareSegment, index: number) =>
  segment.id === null ? OTHERS_COLOR : (SEGMENT_COLORS[index] ?? OTHERS_COLOR)

/**
 * Who carries the church (F3): each branch's share of church income as one segmented bar and a legend in
 * percent. Percentages only, so the tile is the same for a reader branch autonomy withholds the amounts from.
 */
export function ChurchShareTile({
  segments,
  fallback = null,
}: {
  segments: readonly ShareSegment[]
  /** Set when the tile shows the previous period because this one has no money yet: its label, bars muted. */
  fallback?: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const top = segments[0]

  return (
    <HomeTile title={t("widgets.churchShare.title")} subtitle={fallback?.label ?? t("widgets.churchShare.subtitle")} testId='church-tile-share'>
      {!top ? (
        <HomeTileEmpty message={t("church.share.empty")} />
      ) : (
        <>
          <div aria-hidden data-fallback={fallback ? true : undefined} className={cn("flex gap-0.5", fallback && FALLBACK_MUTED_CLASS)}>
            {segments.map((segment, index) => (
              <span
                key={segment.id ?? "others"}
                className={`h-4 rounded-[3px] ${colorOf(segment, index)}`}
                style={{ width: `${segment.percent}%` }}
              />
            ))}
          </div>
          <ul className='flex flex-col gap-1.5'>
            {segments.map((segment, index) => (
              <li key={segment.id ?? "others"} data-testid='church-share-row' className='flex items-center gap-2 text-[12.5px]'>
                <span aria-hidden className={`size-2.5 shrink-0 rounded-[3px] ${colorOf(segment, index)}`} />
                <span className='truncate text-muted-foreground'>{segment.name}</span>
                <span className='ml-auto font-bold tabular-nums'>{t("church.share.percent", { value: Math.round(segment.percent) })}</span>
              </li>
            ))}
          </ul>
          {top.id !== null ? (
            <p className='mt-auto text-xs text-muted-foreground'>
              {t("church.share.sentence", { name: top.name, value: Math.round(top.percent) })}
            </p>
          ) : null}
        </>
      )}
    </HomeTile>
  )
}
